// AGC: WhatsApp phone normalization (Brazil + Argentina). Must stay in sync with
// AGC: packages/app/backend/services/phoneNormalizer.js — same canonical form, same rules.
// AGC: Canonical form is always 13-char E.164:
// AGC:   Brazil:    +55 DD 9 XXXXXXXX   (e.g. +5551985946053)
// AGC:   Argentina: +54 9 [area][local] (e.g. +5493512083999) — leading "9" is mandatory for mobile on WhatsApp.

export type PhoneCountry = 'BR' | 'AR'

const AR_SUBSCRIBER_DIGITS = 10
const BR_BODY_DIGITS = 11

/**
 * AGC: Normalize any WhatsApp phone number to canonical 13-char E.164.
 * AGC: Returns '' (not null) on invalid input so existing callers keep truthy-check semantics.
 */
export const normalizePhone = (raw: string, defaultCountry: PhoneCountry = 'BR'): string => {
  if (!raw || typeof raw !== 'string') return ''

  const digits = raw.replace(/^whatsapp:/i, '').replace(/\D/g, '')
  if (!digits) return ''

  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    return normalizeBRBody(digits.slice(2))
  }
  if (digits.startsWith('54') && (digits.length === 12 || digits.length === 13)) {
    return normalizeARBody(digits.slice(2))
  }
  if (digits.startsWith('0') && digits.length === 11) {
    return normalizeARBody(digits.slice(1))
  }
  if (defaultCountry === 'AR') return normalizeARBody(digits)
  return normalizeBRBody(digits)
}

const normalizeBRBody = (body: string): string => {
  if (body.length === BR_BODY_DIGITS) return `+55${body}`
  if (body.length === 10) return `+55${body.slice(0, 2)}9${body.slice(2)}`
  return ''
}

const normalizeARBody = (body: string): string => {
  if (body.startsWith('9') && body.length === AR_SUBSCRIBER_DIGITS + 1) return `+54${body}`
  if (body.length === AR_SUBSCRIBER_DIGITS) return `+549${body}`
  return ''
}

/**
 * AGC: Detect country code from a canonical phone, or from any raw input (normalizes first).
 */
export const detectCountryFromPhone = (phone: string): PhoneCountry | null => {
  if (typeof phone !== 'string') return null
  const c = /^\+\d+$/.test(phone) ? phone : normalizePhone(phone)
  if (/^\+55\d{11}$/.test(c)) return 'BR'
  if (/^\+549\d{10}$/.test(c)) return 'AR'
  return null
}

/**
 * AGC: Validate that a string is a canonical WhatsApp phone (BR or AR).
 */
export const isValidPhone = (phone: string): boolean => detectCountryFromPhone(phone) !== null

/**
 * AGC: Format canonical phone for display per country.
 * AGC:   BR: +55 (DD) 9XXXX-XXXX
 * AGC:   AR: +54 9 XXX XXX-XXXX
 */
export const formatPhoneForDisplay = (phone: string): string => {
  const c = normalizePhone(phone)
  if (!c) return phone
  const br = c.match(/^\+55(\d{2})(\d{5})(\d{4})$/)
  if (br) return `+55 (${br[1]}) ${br[2]}-${br[3]}`
  const ar = c.match(/^\+549(\d{3})(\d{3})(\d{4})$/)
  if (ar) return `+54 9 ${ar[1]} ${ar[2]}-${ar[3]}`
  return c
}

/**
 * AGC: User-friendly format hint for phone input, chosen per country.
 */
export const getPhoneFormatHint = (country?: PhoneCountry | string | null): string => {
  if (country === 'AR') return '+54 9 351 208-3999'
  return '+55 (41) 99999-8673'
}

// AGC: ─── Back-compat aliases ──────────────────────────────────────────────────
// AGC: Profile.tsx and any other callers import these names. Canonical form now
// AGC: matches the backend (13 digits with leading 9 for BR), not the old 12-digit form.

/** @deprecated Use normalizePhone() — alias kept for back-compat. */
export const normalizePhoneNumber = (phone: string): string => normalizePhone(phone)

/** @deprecated Use isValidPhone() — now accepts both BR and AR canonical. */
export const isValidBrazilianPhone = (phone: string): boolean => isValidPhone(phone)
